<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import Divider from 'primevue/divider';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import PublicationEmailOptions from '@/components/admin/orders/PublicationEmailOptions.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    order: Object,
    itemsWithContent: Array,
    allSent: Boolean,
    hasConfirmedContent: Boolean,
});

// Store publication date for each item
const publicationDates = ref({});

// Store email sending options (recipients + custom messages) for each item
const emailOptions = ref({});

// Initialize publication dates with existing values or current date
props.itemsWithContent?.forEach(item => {
    if (item.content) {
        publicationDates.value[item.content.id] = item.content.sent_to_portal_at
            ? new Date(item.content.sent_to_portal_at)
            : new Date();
    }
});

// Payment warning state
const showPaymentWarning = ref(false);
const pendingPublicationContentId = ref(null);

const confirmPublication = (contentId) => {
    const publicationDate = publicationDates.value[contentId];

    if (!publicationDate) {
        alert($t('Please select a publication date'));
        return;
    }

    const item = props.itemsWithContent?.find(i => i.content?.id === contentId);
    if (item?.seller_requires_payment && !item?.paid) {
        pendingPublicationContentId.value = contentId;
        showPaymentWarning.value = true;
        return;
    }

    doConfirmPublication(contentId);
};

const doConfirmPublication = (contentId) => {
    const publicationDate = publicationDates.value[contentId];

    // When no publication_recipient is available the email options component
    // is not rendered; omit the flags so the backend keeps its defaults.
    router.post(
        proxy.route ? proxy.route('order-content.publication.confirm', contentId) : `/admin/order-content/${contentId}/publication`,
        {
            sent_to_portal_at: publicationDate,
            ...(emailOptions.value[contentId] || {}),
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming publication...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading(),
        }
    );
};

const proceedWithoutPayment = () => {
    showPaymentWarning.value = false;
    doConfirmPublication(pendingPublicationContentId.value);
    pendingPublicationContentId.value = null;
};

const cancelPaymentWarning = () => {
    showPaymentWarning.value = false;
    pendingPublicationContentId.value = null;
};

const downloadContent = (contentId) => {
    const url = proxy.route ? proxy.route('order-content.download', contentId) : `/admin/order-content/${contentId}/download`;
    window.open(url, '_blank');
};

const goToOrder = () => {
    router.visit(
        proxy.route ? proxy.route('orders.index') : `/admin/orders`,
        {
            onStart: () => showLoading($t('Loading order...')),
            onFinish: () => hideLoading(),
        }
    );
};

const itemsCount = computed(() => props.itemsWithContent?.length || 0);
const sentCount = computed(() => props.itemsWithContent?.filter(item => item.content?.sent_to_portal_at !== null).length || 0);

const goToEditOrder = () => router.visit(proxy.route ? proxy.route('orders.edit', props.order.id) : `/admin/orders/${props.order.id}/edit`);
</script>

<template>
    <div class="grid">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">
                            {{ $t('Publication Confirmation') }}
                        </h1>
                        <p class="text-surface-600 mt-1">
                            {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <Button
                            :label="$t('Edit order')"
                            icon="pi pi-pencil"
                            size="small"
                            outlined
                            @click="goToEditOrder"
                        />
                        <Button
                            :label="$t('Back to Orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="goToOrder"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Confirmed Content') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-send text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Sent to Portal') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ sentCount }}/{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- No Confirmed Content Message -->
        <Card v-if="!hasConfirmedContent" class="mb-6">
            <template #content>
                <div class="text-center py-12">
                    <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-700 mb-2">
                        {{ $t('No confirmed content available') }}
                    </h3>
                    <p class="text-surface-500">
                        {{ $t('Content must be confirmed before it can be sent to the portal.') }}
                    </p>
                </div>
            </template>
        </Card>

        <!-- Items List -->
        <div v-else class="space-y-4">
            <Card v-for="(item, index) in itemsWithContent" :key="item.id">
                <template #header>
                    <div class="p-6 pb-0">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                            <div class="flex items-center gap-3">
                                <span class="font-bold text-surface-900">{{ index + 1 }}</span>
                                <div>
                                    <h3 class="font-semibold text-surface-900">
                                        {{ item.site_url || $t('Site URL not available') }}
                                    </h3>
                                    <p class="text-sm text-surface-600">
                                        {{ item.type_name || '-' }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <Tag v-if="item.canceled_at" :value="$t('Canceled')" severity="danger" />
                                <template v-if="item.content">
                                    <Tag
                                        v-if="item.content.sent_to_portal_at"
                                        :value="$t('Sent to Portal')"
                                        severity="success"
                                        icon="pi pi-check"
                                    />
                                    <Tag
                                        v-else
                                        :value="$t('Pending Publication')"
                                        severity="warn"
                                        icon="pi pi-clock"
                                    />
                                </template>
                            </div>
                        </div>
                    </div>
                </template>

                <template #content>
                    <div v-if="item.content" class="space-y-4">
                        <!-- Site Information Section -->
                        <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" />

                        <!-- Anchors Section -->
                        <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3"
                             class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                <i class="pi pi-link text-blue-700 mr-2"></i>
                                {{ $t('Anchors') }}
                            </h5>

                            <div class="flex flex-col md:flex-row gap-4">
                                <!-- Anchor 1 -->
                                <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 1
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 1
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />

                                <!-- Anchor 2 -->
                                <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 2
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 2
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />

                                <!-- Anchor 3 -->
                                <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 3
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 3
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Language and Confirmation Details -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Language') }}
                                </label>
                                <p class="text-surface-900">{{ item.content.language || '-' }}</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Confirmed At') }}
                                </label>
                                <p class="text-surface-900">
                                    {{ item.content.confirmed_by_admin_at ? new Date(item.content.confirmed_by_admin_at).toLocaleString() : '-' }}
                                </p>
                            </div>
                        </div>

                        <!-- Content Link or File -->
                        <div v-if="item.content.content_link || item.content.content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Content') }}
                            </label>

                            <div v-if="item.content.content_link" class="flex items-center gap-2">
                                <i class="pi pi-link text-primary-500"></i>
                                <a
                                    :href="item.content.content_link"
                                    target="_blank"
                                    class="text-primary-500 hover:text-primary-600 underline break-all"
                                >
                                    {{ item.content.content_link }}
                                </a>
                            </div>

                            <div v-if="item.content.content_link_file" class="flex items-center gap-2">
                                <i class="pi pi-file text-primary-500"></i>
                                <Button
                                    :label="$t('Download File')"
                                    icon="pi pi-download"
                                    size="small"
                                    @click="downloadContent(item.content.id)"
                                />
                            </div>
                        </div>

                        <!-- Observations -->
                        <div v-if="item.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <label class="block text-sm font-medium text-amber-900 mb-2">
                                <i class="pi pi-info-circle mr-1"></i>
                                {{ $t('Observations') }}
                            </label>
                            <p class="text-surface-900">{{ item.observations }}</p>
                        </div>

                        <!-- Publication Status -->
                        <div v-if="item.content.sent_to_portal_at" class="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div class="flex items-center gap-2 text-green-700">
                                <i class="pi pi-check-circle text-xl"></i>
                                <div>
                                    <p class="font-medium">{{ $t('Sent to portal') }}</p>
                                    <p class="text-sm">
                                        {{ new Date(item.content.sent_to_portal_at).toLocaleString() }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Publication Date Input and Confirm Button -->
                        <div v-else class="space-y-3">
                            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                    {{ $t('Select Publication Date') }} <span class="text-red-500">*</span>
                                </label>
                                <Calendar
                                    v-model="publicationDates[item.content.id]"
                                    showIcon
                                    dateFormat="dd/mm/yy"
                                    class="w-full md:w-[400px]"
                                />
                            </div>

                            <PublicationEmailOptions
                                v-if="item.publication_recipient"
                                :recipient="item.publication_recipient"
                                :content-id="item.content.id"
                                @change="payload => emailOptions[item.content.id] = payload"
                            />

                            <div class="flex justify-end">
                                <Button
                                    :label="$t('Confirm Publication')"
                                    icon="pi pi-send"
                                    severity="success"
                                    @click="confirmPublication(item.content.id)"
                                    :disabled="!publicationDates[item.content.id]"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- All Sent Message -->
        <Card v-if="allSent && hasConfirmedContent" class="mt-6">
            <template #content>
                <div class="text-center py-8">
                    <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i class="pi pi-check text-green-600 text-3xl"></i>
                    </div>
                    <h3 class="text-xl font-semibold text-surface-900 mb-2">
                        {{ $t('Order confirmed') }}
                    </h3>
                </div>
            </template>
        </Card>
    </div>

    <!-- Payment Warning Dialog -->
    <Dialog
        v-model:visible="showPaymentWarning"
        modal
        :header="$t('Payment not confirmed')"
        :style="{ width: '480px' }"
        @hide="cancelPaymentWarning"
    >
        <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <i class="pi pi-exclamation-triangle text-orange-600 text-xl"></i>
            </div>
            <div>
                <p class="text-surface-700 mb-2">
                    {{ $t('This seller requires payment before publishing, but this item has not been marked as paid.') }}
                </p>
                <p class="text-surface-500 text-sm">
                    {{ $t('You can proceed anyway or go back to mark it as paid first.') }}
                </p>
            </div>
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                severity="secondary"
                @click="cancelPaymentWarning"
            />
            <Button
                :label="$t('Send anyway')"
                icon="pi pi-send"
                severity="warn"
                @click="proceedWithoutPayment"
            />
        </template>
    </Dialog>

</template>

<style scoped>
.p-tag {
    font-weight: 600;
}
</style>
